import React from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Switch } from 'react-router';
import { Route, RouteComponentProps } from 'react-router-dom';
import { BusyLoader } from 'src/components/BusyLoader/BusyLoader';

import { NotFoundPage } from 'src/components/ErrorPage/NotFoundPage';
import { CURRENT_I18N_KEY } from 'src/i18n.config';
import { redirectToUrl, useRoutesByDataCenter } from 'src/utils/AppRoute';
import { LocalStorage } from 'src/utils/LocalStorage';
import { ALL_LOCALES_FOR_DATA_CENTER } from 'src/utils/Locales';
import { enableInternationalRedirect } from 'src/utils/Region';
import { ensureNoTrailingSlash } from 'src/utils/Urls';

export const InternationalRedirects: React.FC = ({ children }) => {
  const { i18n } = useTranslation();
  const [routeToLocale, redirectRoutes] = useRoutesByDataCenter(ALL_LOCALES_FOR_DATA_CENTER);
  const validBaseRoutes = Object.keys(routeToLocale);
  // enables the flag if the user tries to access with any locale of NA datacenter from a bookmark
  const isInternationalRedirectOn = enableInternationalRedirect();

  if (!isInternationalRedirectOn) {
    return <>{children}</>;
  }

  return (
    <Switch>
      {/* Force remove trailing slash per: https://jasonwatmore.com/post/2020/03/23/react-router-remove-trailing-slash-from-urls#:~:text=The%20solution%20is%20to%20add,URL%20without%20the%20trailing%20slash. */}
      <Route exact strict path="/:url*(/+)" render={props => <Redirect to={`${props.location.pathname.slice(0, -1)}${props.location.search}${props.location.hash}`} />} />

      {/* Setup redirects */}
      {Object.entries(redirectRoutes).map(([ from, to ]) => (
        <Route
          key={from}
          path={`${from}/:page*`}
          render={({ match, location }: RouteComponentProps<{ page?: string }>) => {
            return (<Redirect to={redirectToUrl(to, match.params.page, location)} />);
          }}
        />
      ))}

      {/* Setup routes based on 'country + language' combo */}
      <Route
        path={validBaseRoutes}
        render={({ match }: RouteComponentProps<{ page?: string }>) => {
          const locale = routeToLocale[ensureNoTrailingSlash(match.url).toLowerCase()];

          if (locale?.code && i18n.language !== locale.code) {
            i18n.changeLanguage(locale.code);
            LocalStorage.trySetItem(CURRENT_I18N_KEY, locale.code);
          }

          // This is necessary to prevent a race condition where the children render before the language is updated
          if (i18n.language !== locale.code) { // If the current language does not yet equal the locale code in the path
            return <BusyLoader />;
          }

          return (
            <>{children}</>
          );
        }}
      />

      {/* 404 Page when the 'country + language is invalid */}
      <Route render={() => <NotFoundPage />} />
    </Switch>
  );
};
